<template>
    <div class="pt-6">
        <div class="flex justify-between items-center mb-4">
            <h4 class="text-slate-800 dark:text-slate-100 text-base font-medium">
                Points history
            </h4>
            <div class="flex items-center gap-2">
                <div class="relative" ref="periodDropdown">
                    <button
                        @click="togglePeriodDropdown"
                        class="btn-base btn-white flex items-center gap-1 px-3 py-1 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                        :class="{
                            'border-r border-slate-200 dark:border-slate-700':
                                totalRecords > 50,
                        }"
                    >
                        <Icon
                            icon="calendar"
                            :width="16"
                            :height="16"
                            class="text-slate-400"
                        />
                        {{ displayPeriod }}
                        <Icon
                            icon="chevron-down-small"
                            class="w-4 h-4"
                        />
                    </button>
                    <div
                        v-if="showDropdown"
                        class="absolute top-full right-0 mt-1 w-32 p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm dark:shadow-slate-900/50 z-10"
                    >
                        <button
                            v-for="period in periods"
                            :key="period.value"
                            @click="$emit('select-period', period.value)"
                            class="flex w-full p-2 items-center rounded hover:bg-slate-50 dark:hover:bg-slate-700 text-sm text-slate-700 dark:text-slate-200"
                            :class="{
                                'bg-slate-50 dark:bg-slate-700':
                                    selectedPeriod === period.value,
                            }"
                        >
                            {{ period.label }}
                        </button>
                    </div>
                </div>
                <!-- Navigation arrows - only show if more than 50 records -->
                <div
                    v-if="totalRecords > 50"
                    class="flex items-center"
                >
                    <button
                        @click="$emit('navigate-prev')"
                        class="p-1.5 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-400"
                    >
                        <Icon
                            icon="chevron-left-small"
                            :width="20"
                            :height="20"
                        />
                    </button>
                    <button
                        @click="$emit('navigate-next')"
                        class="p-1.5 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-400"
                    >
                        <Icon
                            icon="chevron-right-small"
                            :width="20"
                            :height="20"
                        />
                    </button>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div
            v-if="loading"
            class="flex items-center justify-center py-12"
        >
            <LoadingState :loading="true" />
        </div>

        <template v-else>
            <!-- Chart -->
            <div
                v-if="hasHistoryData"
                class="relative mb-6"
                style="height: 180px"
            >
                <canvas ref="chartCanvas"></canvas>
            </div>

            <!-- Empty State for Chart -->
            <div
                v-else
                class="flex flex-col items-center justify-center py-8 text-center mb-6"
            >
                <Icon
                    icon="graph"
                    class="w-10 h-10 text-slate-300 mb-3"
                />
                <p class="text-slate-500 dark:text-slate-400 text-sm">
                    No history data available yet.
                </p>
            </div>

            <!-- History List -->
            <div v-if="historyList.length > 0" class="space-y-1">
                <template
                    v-for="(group, monthYear) in groupedHistory"
                    :key="monthYear"
                >
                    <div
                        class="text-sm font-medium text-slate-800 dark:text-slate-100 pt-4 pb-2"
                    >
                        {{ monthYear }}
                    </div>
                    <div
                        v-for="(item, index) in group"
                        :key="index"
                        class="group flex justify-between items-center py-2 px-2 -mx-2 border-b border-slate-100 dark:border-slate-700 rounded transition-colors duration-150"
                        :class="{
                            'hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer':
                                isManualAccount && item.id,
                        }"
                    >
                        <span class="text-sm text-slate-600 dark:text-slate-300">
                            {{ item.dateFormatted }}
                        </span>
                        <div class="flex items-center gap-3">
                            <!-- Edit/Delete icons (only for manual accounts) -->
                            <div
                                v-if="isManualAccount && item.id"
                                class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                            >
                                <button
                                    type="button"
                                    class="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
                                    @click.stop="
                                        $emit('edit-entry', item)
                                    "
                                    title="Edit entry"
                                >
                                    <Icon
                                        icon="pencil"
                                        :width="16"
                                        :height="16"
                                    />
                                </button>
                                <button
                                    type="button"
                                    class="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
                                    @click.stop="
                                        $emit('delete-entry', item)
                                    "
                                    title="Delete entry"
                                >
                                    <Icon
                                        icon="delete"
                                        :width="16"
                                        :height="16"
                                    />
                                </button>
                            </div>
                            <div class="text-right">
                                <div
                                    class="text-sm font-medium text-slate-800 dark:text-slate-100"
                                >
                                    {{
                                        $filters.formatThousand(
                                            item.balance
                                        )
                                    }}
                                </div>
                                <div
                                    class="text-xs min-h-[1rem]"
                                    :class="
                                        item.change !== 0
                                            ? item.change > 0
                                                ? 'text-green-500'
                                                : 'text-slate-500 dark:text-slate-400'
                                            : 'invisible'
                                    "
                                >
                                    <template v-if="item.change !== 0">
                                        {{ item.change > 0 ? '+' : ''
                                        }}{{
                                            $filters.formatThousand(
                                                item.change
                                            )
                                        }}
                                    </template>
                                    <template v-else>&nbsp;</template>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>

                <!-- Show More / Pagination -->
                <div
                    v-if="hasMoreRecords"
                    class="flex justify-between items-center pt-4"
                >
                    <button
                        @click="$emit('show-more')"
                        class="text-sm text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100"
                    >
                        Show {{ remainingRecords }} more
                    </button>
                    <span class="text-sm text-slate-400 dark:text-slate-500">
                        {{ historyList.length }} of
                        {{ totalRecords }} records
                    </span>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { toRaw, markRaw } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import {
    Chart,
    CategoryScale,
    LineController,
    LineElement,
    Filler,
    PointElement,
    LinearScale,
    Tooltip,
} from 'chart.js';

Chart.register(
    CategoryScale,
    LineController,
    LineElement,
    Filler,
    PointElement,
    LinearScale,
    Tooltip
);

const props = defineProps({
    historyData: {
        type: Object,
        default: null,
    },
    loading: {
        type: Boolean,
        required: true,
    },
    isManualAccount: {
        type: Boolean,
        required: true,
    },
    selectedPeriod: {
        type: String,
        required: true,
    },
    periods: {
        type: Array,
        required: true,
    },
    groupedHistory: {
        type: Object,
        required: true,
    },
    historyList: {
        type: Array,
        required: true,
    },
    hasHistoryData: {
        type: Boolean,
        required: true,
    },
    totalRecords: {
        type: Number,
        required: true,
    },
    hasMoreRecords: {
        type: Boolean,
        required: true,
    },
    remainingRecords: {
        type: Number,
        required: true,
    },
    displayPeriod: {
        type: String,
        required: true,
    },
    sidebarOpen: {
        type: Boolean,
        required: true,
    },
});

defineEmits([
    'select-period',
    'navigate-prev',
    'navigate-next',
    'show-more',
    'edit-entry',
    'delete-entry',
]);

const chartCanvas = ref(null);
const periodDropdown = ref(null);
const showDropdown = ref(false);
let chart = null;

function togglePeriodDropdown() {
    showDropdown.value = !showDropdown.value;
}

function closeDropdownOnOutsideClick(event) {
    if (
        periodDropdown.value &&
        !periodDropdown.value.contains(event.target)
    ) {
        showDropdown.value = false;
    }
}

function initChart() {
    if (!chartCanvas.value || !props.hasHistoryData) return;

    destroyChart();

    const ctx = chartCanvas.value.getContext('2d');

    const rawData = toRaw(props.historyData);
    const labels = JSON.parse(
        JSON.stringify(rawData.labels || [])
    ).reverse();
    const data = JSON.parse(
        JSON.stringify(rawData.datasets || [])
    ).reverse();

    const paddedLabels = [...labels];
    const paddedData = [...data];
    if (labels.length > 0) {
        const firstDateParts = labels[0].split('/');
        if (firstDateParts.length === 3) {
            const firstDate = new Date(
                parseInt(firstDateParts[2]),
                parseInt(firstDateParts[0]) - 1,
                parseInt(firstDateParts[1])
            );
            const paddingDate = new Date(firstDate);
            paddingDate.setDate(paddingDate.getDate() - 30);
            const paddingLabel = `${paddingDate.getMonth() + 1}/${paddingDate.getDate()}/${paddingDate.getFullYear()}`;
            paddedLabels.unshift(paddingLabel);
            paddedData.unshift(null);
        }
    }

    const isSinglePoint = data.length === 1;

    const chartLabels = isSinglePoint
        ? [paddedLabels[0], paddedLabels[1], paddedLabels[1]]
        : paddedLabels;
    const chartData = isSinglePoint
        ? [null, paddedData[1], paddedData[1]]
        : paddedData;

    chart = markRaw(
        new Chart(ctx, {
            type: 'line',
            data: {
                labels: chartLabels,
                datasets: [
                    {
                        data: chartData,
                        borderColor: '#F27249',
                        backgroundColor: 'rgba(242, 114, 73, 0.15)',
                        borderWidth: 2,
                        fill: true,
                        tension: 0,
                        pointBackgroundColor: '#F27249',
                        pointBorderColor: '#F27249',
                        pointRadius: 0,
                        pointHoverRadius: 4,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    mode: 'index',
                    intersect: false,
                },
                hover: {
                    mode: 'index',
                    intersect: false,
                },
                scales: {
                    x: {
                        grid: { display: false },
                        border: { display: false },
                        ticks: {
                            font: { family: 'Inter', size: 12 },
                            color: '#94A3B8',
                            maxRotation: 0,
                            autoSkip: true,
                            maxTicksLimit: 4,
                            callback: function (value, index, values) {
                                const label =
                                    this.getLabelForValue(value);
                                if (!label) return '';
                                const parts = label.split('/');
                                if (parts.length !== 3) return label;
                                const month = parseInt(parts[0]) - 1;
                                const year = parts[2];
                                const months = [
                                    'Jan',
                                    'Feb',
                                    'Mar',
                                    'Apr',
                                    'May',
                                    'Jun',
                                    'Jul',
                                    'Aug',
                                    'Sep',
                                    'Oct',
                                    'Nov',
                                    'Dec',
                                ];
                                return months[month] + ' ' + year;
                            },
                        },
                    },
                    y: {
                        beginAtZero: true,
                        border: { display: false },
                        grid: {
                            color: '#E2E8F0',
                            drawTicks: false,
                        },
                        ticks: {
                            font: { family: 'Inter', size: 12 },
                            color: '#94A3B8',
                            padding: 8,
                            maxTicksLimit: 5,
                            callback: function (value) {
                                if (value >= 1000000)
                                    return (
                                        (value / 1000000).toFixed(1) +
                                        'M'
                                    );
                                if (value >= 1000)
                                    return value.toLocaleString();
                                return value;
                            },
                        },
                    },
                },
                plugins: {
                    legend: { display: false },
                    datalabels: { display: false },
                    tooltip: {
                        enabled: true,
                        backgroundColor: '#FFF',
                        titleColor: '#0F172A',
                        titleFont: {
                            family: 'Inter',
                            size: 14,
                            weight: '600',
                        },
                        bodyColor: '#64748B',
                        bodyFont: {
                            family: 'Inter',
                            size: 12,
                        },
                        borderColor: '#E2E8F0',
                        borderWidth: 1,
                        padding: 12,
                        cornerRadius: 8,
                        displayColors: false,
                        caretSize: 6,
                        caretPadding: 8,
                        callbacks: {
                            title: function (context) {
                                const label = context[0].label;
                                const parts = label.split('/');
                                if (parts.length === 3) {
                                    const date = new Date(
                                        parseInt(parts[2]),
                                        parseInt(parts[0]) - 1,
                                        parseInt(parts[1])
                                    );
                                    return date.toLocaleDateString(
                                        'en-US',
                                        {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric',
                                        }
                                    );
                                }
                                return label;
                            },
                            label: function (context) {
                                return (
                                    context.parsed.y.toLocaleString() +
                                    ' points'
                                );
                            },
                        },
                    },
                },
            },
        })
    );
}

function destroyChart() {
    if (chart) {
        chart.destroy();
        chart = null;
    }
}

watch(
    () => props.sidebarOpen,
    (newVal) => {
        if (newVal) {
            nextTick(() => {
                initChart();
            });
        } else {
            destroyChart();
        }
    }
);

watch(
    () => props.historyData,
    () => {
        if (props.sidebarOpen && props.hasHistoryData) {
            nextTick(() => {
                initChart();
            });
        }
    },
    { deep: true }
);

// Close dropdown on select-period emit (parent handles period change, we close dropdown)
watch(
    () => props.selectedPeriod,
    () => {
        showDropdown.value = false;
    }
);

onMounted(() => {
    document.addEventListener('click', closeDropdownOnOutsideClick);
});

onBeforeUnmount(() => {
    document.removeEventListener('click', closeDropdownOnOutsideClick);
    destroyChart();
});
</script>
